import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useAppKitAccount } from '@reown/appkit/react';
import { BACKEND_API_URL } from '../data/constant';

/**
 * Profile / logout menu shown in the navbar when the user is signed in.
 *
 * Replaces the previous implementation that wrapped a <Link> inside a
 * MUI Joy <Option> and attached onClick to <Option> — neither reliably
 * fired, so clicking "Profile" or "Logout" effectively did nothing. This
 * version is a plain <button> trigger with a positioned <ul> dropdown
 * that uses real <button> / <Link> children, so navigation and the
 * logout click both work, and the panel is keyboard-navigable.
 */

export default function UserMenu({ profileName, onLoggedOut }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const { disconnect } = useAppKitAccount();

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Wipe all known auth cookies client-side. The backend's
  // Set-Cookie headers will also clear them on the response,
  // but doing it explicitly here means the local state is
  // consistent even if the fetch never resolves (offline, CORS,
  // 405 from a route registered with the wrong method, etc).
  const clearAllAuthCookies = () => {
    // js-cookie's remove() defaults to the current path; pass
    // path=/ to match what the backend Set-Cookie used.
    const cookieOpts = { path: '/' };
    Cookies.remove('user', cookieOpts);
    Cookies.remove('tier', cookieOpts);
    Cookies.remove('session', cookieOpts);
    Cookies.remove('session_id', cookieOpts);
    // Also clear anything else for the current domain.
    if (typeof document !== 'undefined' && document.cookie) {
      document.cookie
        .split(';')
        .map((c) => c.trim().split('=')[0])
        .filter(Boolean)
        .forEach((name) => {
          document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
          document.cookie = `${name}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
    }
  };

  const handleLogout = async () => {
    setOpen(false);

    // Tell the backend to clear the server-side session. The route
    // is registered as v1.GET("/logout", authMiddleware(), handleLogout)
    // — NOT POST. A POST here returns 405 and the .then never fires.
    // credentials: 'include' is required so the session cookie is
    // actually sent to the server.
    try {
      await fetch(BACKEND_API_URL + 'logout', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch (err) {
      // Log but don't block the logout — we still want the
      // client-side state cleared even if the server can't be
      // reached (offline, already-expired session, etc).
      console.warn('Logout request failed (continuing anyway):', err);
    }

    // Always clear local state, even if the backend call failed
    // or returned a non-2xx status. The user's intent is clear.
    clearAllAuthCookies();

    // Disconnect the Web3 wallet so a re-login flow starts fresh.
    try {
      if (typeof disconnect === 'function') {
        await disconnect();
      }
    } catch (err) {
      console.warn('Wallet disconnect failed (continuing anyway):', err);
    }

    // Tell the parent to clear its loggedIn/profileName state,
    // then navigate to the landing page.
    if (onLoggedOut) onLoggedOut();
    navigate('/');
  };

  const initial = (profileName || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="user-menu">
      <button
        ref={triggerRef}
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-menu__avatar" aria-hidden="true">{initial}</span>
        <span className="user-menu__name">{profileName || 'Account'}</span>
        <svg
          className="user-menu__chevron"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul ref={panelRef} className="user-menu__panel" role="menu">
          <li role="none">
            <Link
              to="/profile"
              role="menuitem"
              className="user-menu__item"
              onClick={() => setOpen(false)}
            >
              Profile
            </Link>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="user-menu__item user-menu__item--danger"
              onClick={handleLogout}
            >
              Log out
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
